import React, { memo } from 'react';
import propTypes from 'prop-types';
import { Columns, Column } from 'src/ui/mui-ui-kit';
import { Field } from 'react-final-form';
import {
  UiInputAdapter,
  UiDatePickerAdapter,
  UiSelectAdapter,
  UiToggleAdapter,
  UiTooltip,
} from 'src/ui';
import {
  composeValidators,
  email,
  ssn,
  maxLength,
  required,
} from 'src/ui/validations';
import { PHONE_MASK, SSN_MASK } from 'src/ui/const';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs-ext';

const maxBirthDate = dayjs()
  .startOf('day')
  .subtract(18, 'year')
  .toDate();

function OwnerInformationForm({
  militaryOptions,
  citizenshipOptions,
  className,
  user,
}) {
  return (
    <Columns isMultiline className={className}>
      {(user.isMerchantAdmin || user.isBankAdmin) && (
        <Column isSize="full" className="owner-toggles">
          <Columns>
            <Column isSize="narrow">
              <Field
                component={UiToggleAdapter}
                id="isAdmin"
                name="isAdmin"
                type="checkbox"
              >
                Account Admin
                <UiTooltip content="Account admins can change business information and invite other people to join" />
              </Field>
            </Column>
            <Column isSize="narrow">
              <Field
                component={UiToggleAdapter}
                id="isSigner"
                name="isSigner"
                type="checkbox"
              >
                Account Signer
                <UiTooltip content="Can sign consents and documents on behalf of the account" />
              </Field>
            </Column>
          </Columns>
        </Column>
      )}
      <Column isSize="1/2" className="is-one-third-fullhd">
        <Field
          component={UiInputAdapter}
          name="firstName"
          id="firstName"
          required
          label="First Name"
        />
      </Column>
      <Column isSize="1/2" className="is-one-third-fullhd">
        <Field
          component={UiInputAdapter}
          name="lastName"
          id="lastName"
          required
          label="Last Name"
        />
      </Column>
      <Column isSize="1/2" className="is-one-third-fullhd">
        <Field
          component={UiInputAdapter}
          name="primaryNumber"
          id="primaryNumber"
          required
          label="Primary Phone"
          mask={PHONE_MASK}
          hasIcons="right"
          renderIcons={() => [
            <span className="icon is-small is-right" key="right-icon">
              <FontAwesomeIcon icon={['fal', 'phone']} size="sm" />
            </span>,
          ]}
        />
      </Column>
      <Column isSize="1/2" className="is-one-third-fullhd">
        <Field
          component={UiInputAdapter}
          name="otherNumber"
          id="otherNumber"
          label="Other Phone"
          mask={PHONE_MASK}
          hasIcons="right"
          renderIcons={() => [
            <span className="icon is-small is-right" key="right-icon">
              <FontAwesomeIcon icon={['fal', 'phone']} size="sm" />
            </span>,
          ]}
        />
      </Column>
      <Column isSize="1/2" className="is-one-third-fullhd">
        <Field
          component={UiInputAdapter}
          name="emailAddress"
          id="emailAddress"
          required
          validate={composeValidators('Email Address', required, email)}
          label="Email Address"
          hasIcons="right"
          renderIcons={() => [
            <span className="icon is-small is-right" key="right-icon">
              <FontAwesomeIcon icon={['fal', 'envelope']} size="sm" />
            </span>,
          ]}
        />
      </Column>
      <Column isSize="1/2" className="is-one-third-fullhd">
        <Field
          component={UiInputAdapter}
          name="percentageOwnership"
          id="percentageOwnership"
          required
          label="% Ownership"
        />
      </Column>
      <Column isSize="1/2" className="is-one-third-fullhd">
        <Field
          component={UiInputAdapter}
          name="socialSecurity"
          id="socialSecurity"
          required
          validate={composeValidators('Social Security #', ssn)}
          label="Social Security Number"
          mask={SSN_MASK}
        />
      </Column>
      <Column isSize="1/2" className="is-one-third-fullhd">
        <Field
          component={UiDatePickerAdapter}
          name="dob"
          id="dob"
          label="Date of Birth"
          required
          maxDate={maxBirthDate}
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          hasIcons="right"
          renderIcons={() => [
            <span className="icon is-small is-right" key="right-icon">
              <FontAwesomeIcon icon={['fal', 'calendar-alt']} size="sm" />
            </span>,
          ]}
        />
      </Column>
      <Column isSize="1/2" className="is-one-third-fullhd">
        <Field
          component={UiInputAdapter}
          name="occupation"
          id="occupation"
          required
          label="Occupation"
          validate={composeValidators('Occupation', maxLength(32))}
          maxlen="32"
        />
      </Column>
      <Column isSize="1/2" className="is-one-third-fullhd">
        <Field
          component={UiInputAdapter}
          name="title"
          id="title"
          label="Title"
          validate={composeValidators('Title', maxLength(32))}
        />
      </Column>
      <Column isSize="1/2" className="is-one-third-fullhd">
        <Field
          component={UiSelectAdapter}
          name="military"
          id="military"
          options={militaryOptions}
          label="Military"
        />
      </Column>
      <Column isSize="1/2" className="is-one-third-fullhd">
        <Field
          component={UiSelectAdapter}
          name="citizenship"
          id="citizenship"
          required
          options={citizenshipOptions}
          label="Citizenship"
        />
      </Column>
    </Columns>
  );
}

OwnerInformationForm.defaultProps = {
  className: '',
};

OwnerInformationForm.propTypes = {
  militaryOptions: propTypes.arrayOf(
    propTypes.shape({
      label: propTypes.string.isRequired,
      value: propTypes.string.isRequired,
    })
  ).isRequired,
  citizenshipOptions: propTypes.arrayOf(
    propTypes.shape({
      label: propTypes.string.isRequired,
      value: propTypes.string.isRequired,
    })
  ).isRequired,
  className: propTypes.string,
  user: propTypes.object.isRequired,
};

const memoizedOwnerInformationForm = memo(OwnerInformationForm);

export { memoizedOwnerInformationForm as OwnerInformationForm };
